import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { User, LogOut, Globe, ChevronDown, Menu as MenuIcon, CalendarDays } from 'lucide-react';

import { useAuth } from '../../context/AuthContext';
import Avatar from '../ui/Avatar';
import AiChatButton from '../ai/AiChatButton';
import AiChatModal from '../ai/AiChatModal';
import TenantSwitcher from './TenantSwitcher';
import IconButton from '../ui/IconButton';
import GradientIcon from '../ui/GradientIcon';
import Tooltip from '../ui/Tooltip';
// SIDEBAR imports removed — Header is no longer fixed/positioned

const languages = [
  { code: 'tr', label: 'Türkçe', flag: '🇹🇷' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'ru', label: 'Русский', flag: '🇷🇺' },
  { code: 'kk', label: 'Қазақша', flag: '🇰🇿' },
  { code: 'az', label: 'Azərbaycanca', flag: '🇦🇿' },
];

const Container = styled.header`
  height: 56px;
  background: #fff;
  border-bottom: 1px solid #E8ECF0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  /* FE-08 — dar ekranda 16px'lik yatay boşluk selamlama metninden çalıyor. */
  padding: 0 ${({ $compact }) => ($compact ? 10 : 16)}px;
  gap: 8px;
  flex-shrink: 0;
`;

// Takvimim düğmesi — AiChatButton ile aynı ölçü/davranış (38 px yuvarlak, üzerine gelince büyür);
// kullanıcı sayfadayken hafif vurgulu zeminde kalır.
const CalendarButton = styled.button`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  border: none;
  background: ${({ $active, theme }) => ($active ? theme.colors.calendar.headerActiveBg : 'transparent')};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: transform 0.2s ease, background 0.15s;

  &:hover {
    transform: scale(1.08);
    background: ${({ theme }) => theme.colors.calendar.headerHoverBg};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary.main};
    outline-offset: 2px;
  }
`;

// FE-08 — sol küme: hamburger + selamlama. `min-width: 0` ŞART, aksi halde
// flex çocuğun varsayılan `min-width: auto`'su selamlama metninin kısalmasını
// engeller ve sağdaki profil/dil kümesini ekran dışına iter.
const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  flex: 1;
`;

const WelcomeText = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.md};
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  /* Uzun ad + uzun kiracı adı dar ekranda başlığı iki satıra kırıp 56px'lik
     şeridi taşırıyordu; tek satır + üç nokta. */
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  /* 20px × 4 öğe = 60px; 390px'lik bir başlıkta bu tek başına %15. */
  gap: ${({ $compact }) => ($compact ? 6 : 20)}px;
  flex-shrink: 0;
`;

const DateText = styled.span`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.dark.little};
  font-weight: ${({ theme }) => theme.metrics.font.weight.regular};
  white-space: nowrap;

  /* Tarih kabuk süsüdür — telefonda yerini profil ve dil seçicisine bırakır. */
  @media (max-width: 600px) {
    display: none;
  }
`;

const ProfileButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: ${({ theme }) => theme.metrics.radius.medium};
  transition: background 0.15s;

  &:hover {
    background: ${({ theme }) => theme.colors.bgColor};
  }
`;

const ProfileName = styled.span`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};

  @media (max-width: 768px) {
    display: none;
  }
`;

const DropdownWrapper = styled.div`
  position: relative;
`;

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 220px;
  background: #fff;
  border-radius: ${({ theme }) => theme.metrics.radius.medium};
  box-shadow: 0 8px 32px rgba(0, 66, 66, 0.12);
  overflow: hidden;
  z-index: 200;
`;

const DropdownHeader = styled.div`
  padding: 16px;
  border-bottom: 1px solid #E8ECF0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const DropdownUserInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const DropdownName = styled.span`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const DropdownEmail = styled.span`
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

const DropdownItem = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 12px 16px;
  background: none;
  border: none;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: ${({ theme }) => theme.colors.bgColor};
  }
`;

const DropdownDivider = styled.div`
  height: 1px;
  background: #E8ECF0;
`;

const LanguageSubmenu = styled.div`
  padding: 4px 0;
  border-top: 1px solid #E8ECF0;
`;

const LanguageOption = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 16px 8px 42px;
  background: ${({ $active, theme }) => ($active ? 'rgba(2, 145, 239, 0.06)' : 'none')};
  border: none;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: rgba(2, 145, 239, 0.08);
  }
`;

const LogoutItem = styled(DropdownItem)`
  color: ${({ theme }) => theme.colors.auxiliar.danger};
`;

// FE-08 — `showMenuButton`: dar ekranda kenar çubuğu off-canvas olur ve onu
// açacak TEK giriş noktası buradaki hamburgerdir. `compact`: 600px altında
// başlık şeridinin boşlukları daraltılır.
export default function Header({ showMenuButton = false, compact = false, onMenuClick }) {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { user, logout, isPreboardingLocked } = useAuth();
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [aiChatOpen, setAiChatOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const now = new Date();
  const dayName = t(`days.${now.getDay()}`);
  const monthName = t(`months.${now.getMonth()}`);
  const dateStr = `${now.getDate()} ${monthName} ${dayName}`;

  const fullName = user?.fullName || `${user?.firstName || ''} ${user?.lastName || ''}`.trim();
  const firstName = user?.firstName || fullName?.split(' ')[0] || '';
  const lastName = user?.lastName || fullName?.split(' ').slice(1).join(' ') || '';

  const handleLogout = async () => {
    setOpen(false);
    await logout();
  };

  const handleLanguageChange = (code) => {
    i18n.changeLanguage(code);
    setLangOpen(false);
  };

  return (
    <Container $compact={compact}>
      <LeftSection>
        {showMenuButton && (
          <IconButton
            variant="neutral"
            size="sm"
            onClick={onMenuClick}
            aria-label={t('layout.openMenu')}
            title={t('layout.openMenu')}
            data-testid="sidebar-toggle"
          >
            <MenuIcon size={18} />
          </IconButton>
        )}
        <WelcomeText>
          {t('layout.welcome')}, {firstName || user?.email}
        </WelcomeText>
      </LeftSection>
      <RightSection $compact={compact}>
        <TenantSwitcher />
        {/* Takvimim — herkese açık self-servis (modül kapısı yok); işe hazırlık kilidinde gizli. */}
        {!isPreboardingLocked && (
          <Tooltip text={t('myCalendar.title')} position="bottom">
            <CalendarButton
              type="button"
              aria-label={t('myCalendar.title')}
              data-testid="header-calendar"
              $active={location?.pathname === '/my-calendar'}
              onClick={() => history.push('/my-calendar')}
            >
              <GradientIcon icon={CalendarDays} size={22} />
            </CalendarButton>
          </Tooltip>
        )}
        <AiChatButton onClick={() => setAiChatOpen(true)} />
        <DateText>{dateStr}</DateText>
        <DropdownWrapper ref={ref}>
          <ProfileButton onClick={() => { setOpen(!open); setLangOpen(false); }}>
            <Avatar
              src={user?.profilePicture}
              firstName={firstName || '?'}
              lastName={lastName || '?'}
              size={36}
            />
            <ProfileName>{firstName}</ProfileName>
            <ChevronDown size={14} color="#A0A8B4" />
          </ProfileButton>
          {open && (
            <Dropdown>
              <DropdownHeader>
                <Avatar
                  src={user?.profilePicture}
                  firstName={firstName || '?'}
                  lastName={lastName || '?'}
                  size={40}
                />
                <DropdownUserInfo>
                  <DropdownName>{fullName}</DropdownName>
                  <DropdownEmail>{user?.email}</DropdownEmail>
                </DropdownUserInfo>
              </DropdownHeader>
              <DropdownItem onClick={() => { setOpen(false); history.push('/profile'); }}>
                <User size={16} />
                {t('layout.profilePage')}
              </DropdownItem>
              <DropdownItem onClick={() => setLangOpen(!langOpen)}>
                <Globe size={16} />
                {t('layout.language')}
              </DropdownItem>
              {langOpen && (
                <LanguageSubmenu>
                  {languages.map((lang) => (
                    <LanguageOption
                      key={lang.code}
                      $active={lang.code === i18n.language}
                      onClick={() => handleLanguageChange(lang.code)}
                    >
                      {lang.flag} {lang.label}
                    </LanguageOption>
                  ))}
                </LanguageSubmenu>
              )}
              <DropdownDivider />
              <LogoutItem onClick={handleLogout}>
                <LogOut size={16} />
                {t('common.logout')}
              </LogoutItem>
            </Dropdown>
          )}
        </DropdownWrapper>
      </RightSection>
      <AiChatModal open={aiChatOpen} onClose={() => setAiChatOpen(false)} />
    </Container>
  );
}
